import React, { useEffect, useMemo } from "react";
import { Box, CardContent, Grid } from "@mui/material";
import {
  BorderGreyBox,
  CardSpg,
  handleSubmitValidationFail,
  isNotEmpty,
  NameFilter,
  IsActiveFilter,
  AddressFilter,
  ZipCodeFilter,
  WebsiteFilter,
  ScrollableAsyncFormAutocompleteFilter,
  useReactHookForm,
} from "@duosoftbg/nacid-components";

import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useStore } from "react-redux";
import { SEARCH_FILTERS_GROUP } from "../../../../config/search/filters/groupsConfig";
import useSearchTableControl from "../../../../hooks/portal/search/useSearchTableControl";
import SearchFilters from "../../common/search/filters/SearchFilters";
import { getSettlementsAutocomplete, searchNationalUniversities } from "../../../../axios/api/services";
import { NationalUniversityFilterDetails } from "../../../../types/nationalUniversityTypes";
import { nationalUniversityFilterInitialValues } from "../../../../init/nationalUniversityFilterInitialValues";
import { createNationalUniversityFilterValidationSchema } from "../../../../yup/schema/nationalUniversity/nationalUniversityFilterValidationSchema";
import NationalUniversityDataList from "./NationalUniversityDataList";
import { RootState } from "../../../../store/redux/store";

const NationalUniversityDataFilters = () => {
  return (
    <Grid container spacing={5} px={2} py={6}>
      <NameFilter />
      <AddressFilter />
      <ZipCodeFilter />
      <WebsiteFilter />
      <IsActiveFilter />
      <ScrollableAsyncFormAutocompleteFilter
        fieldName={"settlementCode"}
        setOptionText={(option) => option.optionName}
        getOptionLabel={(option) => option.id}
        setInputOnSelect={(option) => option.selectedOptionValue}
        autocompleteFn={getSettlementsAutocomplete}
        label={"l.settlement"}
        reduceOptionObject={false}
      />
    </Grid>
  );
};

const NationalUniversityData = () => {
  const { t } = useTranslation();
  const store = useStore();
  const group = SEARCH_FILTERS_GROUP.NATIONAL_UNIVERSITY;

  const { methods } = useReactHookForm<NationalUniversityFilterDetails>({
    defaultValues: nationalUniversityFilterInitialValues,
    validationSchema: createNationalUniversityFilterValidationSchema,
  });

  useEffect(() => {
    const filtersData = (store.getState() as RootState).SearchData.portalSearchTable[group].filtersData;
    if (isNotEmpty(filtersData)) {
      methods.reset(filtersData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const control = useSearchTableControl({
    group,
    methods,
    initialValues: nationalUniversityFilterInitialValues,
    filterData: searchNationalUniversities,
    callAfterResetFilters: true,
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(
          () => control.handleSubmitFilters(),
          () => handleSubmitValidationFail(methods.formState, t, undefined),
        )}
      >
        <CardSpg my={4} style={{ overflow: "visible" }}>
          <CardContent style={{ position: "relative" }}>
            <Box>
              <BorderGreyBox>
                <SearchFilters
                  isSubmitBtnDisabled={control.isSubmitBtnDisabled}
                  isResetBtnDisabled={control.isResetBtnDisabled}
                  handleResetFilters={control.handleResetFilters}
                >
                  {useMemo(
                    () => (
                      <NationalUniversityDataFilters />
                    ),
                    [],
                  )}
                </SearchFilters>
              </BorderGreyBox>
              {control.showList && (
                <NationalUniversityDataList
                  records={control.records}
                  total={control.total}
                  isLoading={control.isLoading}
                  onPageOrOrderChange={control.handlePageOrOrderChange}
                  blockTable={control.blockTable}
                  group={group}
                  filterFn={control.fetch}
                />
              )}
            </Box>
          </CardContent>
        </CardSpg>
      </form>
    </FormProvider>
  );
};

export default NationalUniversityData;
